import { useState } from "react";
import {
  Download,
  File,
  FileArchive,
  FileImage,
  FileSpreadsheet,
  FileText,
  Loader2,
  type LucideIcon,
} from "lucide-react";
import { toast } from "sonner";
import { TipoArquivo, TipoArquivoMap } from "@workflow/types";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { arquivoService } from "@/services/api/ArquivoService";

/**
 * Paleta de "tons" (border + bg suave + texto) reutilizável. Strings completas
 * porque o Tailwind não resolve classes montadas dinamicamente.
 */
const TONES = {
  slate: "border-slate-500/20 bg-slate-500/[0.07] text-slate-700 dark:text-slate-300",
  blue: "border-blue-500/20 bg-blue-500/[0.07] text-blue-700 dark:text-blue-300",
  emerald: "border-emerald-500/20 bg-emerald-500/[0.07] text-emerald-700 dark:text-emerald-300",
  amber: "border-amber-500/20 bg-amber-500/[0.07] text-amber-700 dark:text-amber-300",
  violet: "border-violet-500/20 bg-violet-500/[0.07] text-violet-700 dark:text-violet-300",
  indigo: "border-indigo-500/20 bg-indigo-500/[0.07] text-indigo-700 dark:text-indigo-300",
  rose: "border-rose-500/20 bg-rose-500/[0.07] text-rose-700 dark:text-rose-300",
  orange: "border-orange-500/20 bg-orange-500/[0.07] text-orange-700 dark:text-orange-300",
  sky: "border-sky-500/20 bg-sky-500/[0.07] text-sky-700 dark:text-sky-300",
  teal: "border-teal-500/20 bg-teal-500/[0.07] text-teal-700 dark:text-teal-300",
  fuchsia: "border-fuchsia-500/20 bg-fuchsia-500/[0.07] text-fuchsia-700 dark:text-fuchsia-300",
  cyan: "border-cyan-500/20 bg-cyan-500/[0.07] text-cyan-700 dark:text-cyan-300",
  red: "border-red-500/20 bg-red-500/[0.07] text-red-700 dark:text-red-300",
} as const;

type Tone = keyof typeof TONES;

// --- Tipo de arquivo: cor por categoria ------------------------------------
const TIPO_TONE: Record<TipoArquivo, Tone> = {
  [TipoArquivo.INTERNO]: "blue",
  [TipoArquivo.COMERCIAL]: "emerald",
  [TipoArquivo.APROVACAO]: "amber",
  [TipoArquivo.AP]: "violet",
  [TipoArquivo.PO]: "indigo",
  [TipoArquivo.NF]: "rose",
  [TipoArquivo.COMPRAS]: "orange",
  [TipoArquivo.JOB_BRIEFING]: "sky",
  [TipoArquivo.JOB_PAPER]: "teal",
  [TipoArquivo.JOB_PROJETO_EXECUTIVO]: "teal",
  [TipoArquivo.JOB_PROJETO_FINAL]: "teal",
  [TipoArquivo.JOB_ESTUDO_VERBA]: "teal",
  [TipoArquivo.JOB_PLANEJAMENTO_APRESENTACAO]: "fuchsia",
  [TipoArquivo.COMPROVANTE_ENTREGA_FATURAMENTO]: "cyan",
  [TipoArquivo.TAREFA]: "slate",
  [TipoArquivo.EVIDENCIA_BRINDE]: "cyan",
};

export function TipoBadge({ tipo }: { tipo: TipoArquivo }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-medium",
        TONES[TIPO_TONE[tipo]] ?? TONES.slate,
      )}
    >
      {TipoArquivoMap[tipo] || tipo}
    </span>
  );
}

// --- Extensão: ícone + cor por família de arquivo --------------------------
interface ExtVisual {
  icon: LucideIcon;
  tone: Tone;
  /** Cor sólida do ícone (mais viva que o tom de fundo). */
  iconClass: string;
}

const EXT_GROUPS: { exts: string[]; visual: ExtVisual }[] = [
  {
    exts: ["pdf"],
    visual: { icon: FileText, tone: "red", iconClass: "text-red-500" },
  },
  {
    exts: ["xls", "xlsx", "xlsb", "xlsm", "csv"],
    visual: { icon: FileSpreadsheet, tone: "emerald", iconClass: "text-emerald-500" },
  },
  {
    exts: ["doc", "docx"],
    visual: { icon: FileText, tone: "blue", iconClass: "text-blue-500" },
  },
  {
    exts: ["png", "jpg", "jpeg", "webp", "gif", "svg"],
    visual: { icon: FileImage, tone: "violet", iconClass: "text-violet-500" },
  },
  {
    exts: ["zip", "rar", "7z"],
    visual: { icon: FileArchive, tone: "amber", iconClass: "text-amber-500" },
  },
];

const DEFAULT_EXT_VISUAL: ExtVisual = {
  icon: File,
  tone: "slate",
  iconClass: "text-muted-foreground",
};

function getExtVisual(extensao?: string): ExtVisual {
  if (!extensao) return DEFAULT_EXT_VISUAL;
  const normalized = extensao.toLowerCase().replace(/^\./, "");
  return EXT_GROUPS.find((g) => g.exts.includes(normalized))?.visual ?? DEFAULT_EXT_VISUAL;
}

/** Ícone de arquivo colorido conforme a extensão. */
export function FileTypeIcon({ extensao, className }: { extensao?: string; className?: string }) {
  const { icon: Icon, iconClass } = getExtVisual(extensao);
  return <Icon className={cn("h-4 w-4 shrink-0", iconClass, className)} aria-hidden />;
}

/** Tag curta da extensão (ex.: PDF, XLSB) colorida por família. */
export function ExtensaoTag({ extensao }: { extensao?: string }) {
  if (!extensao) return null;
  const { tone } = getExtVisual(extensao);
  return (
    <span
      className={cn(
        "shrink-0 rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
        TONES[tone],
      )}
    >
      {extensao.replace(/^\./, "")}
    </span>
  );
}

/** Botão de download de um arquivo: busca a URL assinada e abre em nova aba. */
export function ArquivoDownloadButton({ arquivoId, nome }: { arquivoId: number; nome?: string }) {
  const [isLoading, setIsLoading] = useState(false);

  const handleDownload = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const url = await arquivoService.downloadArquivo(arquivoId);
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        toast.error("Não foi possível obter o link do arquivo.");
      }
    } catch (error) {
      console.error("Erro ao baixar arquivo", error);
      toast.error("Erro ao baixar o arquivo.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      variant="ghost"
      size="icon"
      className="h-8 w-8 text-muted-foreground hover:text-foreground"
      onClick={handleDownload}
      disabled={isLoading}
      title={nome ? `Baixar ${nome}` : "Baixar arquivo"}
    >
      <span className="sr-only">Baixar arquivo</span>
      {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
    </Button>
  );
}

/** Célula completa de nome de arquivo: ícone colorido + nome + tag de extensão. */
export function ArquivoNomeCell({ nome, extensao }: { nome: string; extensao?: string }) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <FileTypeIcon extensao={extensao} />
      <span className="truncate font-medium text-foreground" title={nome}>
        {nome}
      </span>
      <ExtensaoTag extensao={extensao} />
    </div>
  );
}
